import { createContext, useContext, useState } from 'react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 5000, options = {}) => {
    const id = Date.now();
    const toast = {
      id,
      message,
      type,
      duration,
      title: options.title,
      showActions: options.showActions || false,
      onProceed: options.onProceed,
      onCancel: options.onCancel
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration (only if no actions or custom duration)
    if (duration > 0 && !options.showActions) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (message, duration, title) => addToast(message, 'success', duration, { title });
  const error = (message, duration, title) => addToast(message, 'error', duration, { title });
  const info = (message, duration, title) => addToast(message, 'info', duration, { title });
  const warning = (message, duration, title) => addToast(message, 'warning', duration, { title });

  const confirm = (message, onProceedAsync, type = 'warning', title = null, successButton = 'OK') => {
    return new Promise((resolve) => {
      const id = Date.now();

      const handleProceed = async () => {
        if (onProceedAsync) {
          await onProceedAsync();
        }
        resolve(true);
      };

      const handleCancel = () => {
        resolve(false);
      };

      // Handle successButton as either string or object
      const buttonConfig = typeof successButton === 'string'
        ? { title: successButton, titleInProgress: 'Loading...' }
        : { title: successButton.title || 'OK', titleInProgress: successButton.titleInProgress || 'Loading...' };

      const toast = {
        id,
        message,
        type,
        duration: 0, // No auto-dismiss
        title,
        showActions: true,
        buttonText: buttonConfig.title,
        loadingText: buttonConfig.titleInProgress,
        onProceed: handleProceed,
        onCancel: handleCancel
      };

      setToasts(prev => [...prev, toast]);
    });
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    info,
    warning,
    confirm
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};